// Uses the Window Management API where available.
// Docs: https://developer.mozilla.org/en-US/docs/Web/API/Window_Management_API

export interface DualScreenStatus {
  supported: boolean;
  screenCount: number;
  isSingle: boolean;
  reason?: string;
}

interface ScreenDetailed { }
interface ScreenDetails {
  screens: ScreenDetailed[];
  addEventListener?: (t: string, l: () => void) => void;
  removeEventListener?: (t: string, l: () => void) => void;
}
type Win = Window & { getScreenDetails?: () => Promise<ScreenDetails> };

export async function checkDualScreen(): Promise<DualScreenStatus> {
  const w = window as Win;
  if (typeof w.getScreenDetails !== "function") {
    // Fallback heuristic — width thresholds are unreliable; treat as single.
    return { supported: false, screenCount: 1, isSingle: true, reason: "Window Management API unavailable" };
  }
  try {
    const details = await w.getScreenDetails();
    const n = details.screens?.length ?? 1;
    return { supported: true, screenCount: n, isSingle: n <= 1 };
  } catch (e: any) {
    // Permission denied or blocked. Best-effort fallback.
    return {
      supported: false,
      screenCount: 1,
      isSingle: true,
      reason: e?.message ?? "Screen enumeration permission denied",
    };
  }
}

export async function watchScreenChanges(onChange: (s: DualScreenStatus) => void): Promise<() => void> {
  const w = window as Win;
  if (typeof w.getScreenDetails !== "function") return () => {};
  try {
    const details = await w.getScreenDetails();
    const handler = () => {
      const n = details.screens?.length ?? 1;
      onChange({ supported: true, screenCount: n, isSingle: n <= 1 });
    };
    details.addEventListener?.("screenschange", handler);
    return () => details.removeEventListener?.("screenschange", handler);
  } catch {
    return () => {};
  }
}
